import { useMemo, useState } from "react";
import {
  ArrowUpRight,
  BadgeCheck,
  BookOpen,
  CalendarDays,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  Compass,
  ExternalLink,
  FileText,
  Filter,
  Flame,
  Globe2,
  HeartPulse,
  Home,
  Landmark,
  LayoutDashboard,
  Library,
  MapPinned,
  Route,
  Search,
  ShieldAlert,
  Sparkles,
  TableProperties,
  UsersRound,
} from "lucide-react";
import {
  bucketListCategories,
  defenses,
  fieldNotes,
  lifestylePaths,
  navigationSections,
  positioning,
  resourceCategories,
} from "./data/siteContent";
import {
  activeAdultSummary,
  dashboardMetrics,
  locationRows,
  painSummary,
  resourceRows,
} from "./data/atlasData";

type AtlasTab = "places" | "pain" | "communities" | "resources";

const atlasTabs: Array<{
  id: AtlasTab;
  label: string;
  icon: typeof MapPinned;
}> = [
  { id: "places", label: "Locations", icon: MapPinned },
  { id: "pain", label: "Pain Signals", icon: HeartPulse },
  { id: "communities", label: "55+ Inventory", icon: Home },
  { id: "resources", label: "Source Files", icon: Library },
];

const icpCards = [
  {
    title: "Closest painful fit",
    icon: Flame,
    body: "A 58-72 year-old household with enough assets to have options, but not enough certainty to treat retirement as a vacation problem. They are deciding whether to quit, move, buy into a community, help family, or retire abroad.",
  },
  {
    title: "Decision moment",
    icon: ClipboardCheck,
    body: "The pain spikes before irreversible moves: selling the house, choosing a 55+ community, changing states, picking a Medicare/insurance path, or trusting a commission-shaped recommendation.",
  },
  {
    title: "Message",
    icon: BadgeCheck,
    body: "Before you move, buy, quit, or sign, pressure-test the life you are buying. Next Chapter turns vague retirement anxiety into a place, money, health, family, and purpose dossier.",
  },
];

const eventLenses = [
  {
    label: "Low friction",
    icon: HeartPulse,
    text: "Events that still work during lower-energy weeks: accessible routes, matinees, standing meals, library classes, gentle trips.",
  },
  {
    label: "Seasonal",
    icon: CalendarDays,
    text: "A retirement calendar with recurring rituals so time does not flatten after work stops supplying quarters and deadlines.",
  },
  {
    label: "Legacy",
    icon: FileText,
    text: "Creative, family, and service projects with a container, a finish line, and somebody who will actually receive the work.",
  },
  {
    label: "Place tests",
    icon: MapPinned,
    text: "Trips that double as evidence: healthcare radius, walkability, family rhythm, food access, weather, and ordinary Tuesday checks.",
  },
];

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatMoney(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function includesQuery(values: unknown[], query: string) {
  if (!query.trim()) return true;
  const haystack = values
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .join(" ")
    .toLowerCase();
  return haystack.includes(query.trim().toLowerCase());
}

function categoryIcon(category: "planning" | "scam" | "risk") {
  if (category === "planning") return ClipboardCheck;
  if (category === "scam") return ShieldAlert;
  return HeartPulse;
}

function App() {
  const [activeTab, setActiveTab] = useState<AtlasTab>("places");
  const [query, setQuery] = useState("");
  const [selectedPath, setSelectedPath] = useState(lifestylePaths[0]?.id ?? "");

  const selectedLifestyle = lifestylePaths.find((path) => path.id === selectedPath) ?? lifestylePaths[0];

  const filteredLocations = useMemo(
    () =>
      locationRows.filter((row) =>
        includesQuery(
          [
            row.location,
            row.region,
            row.archetype,
            row.bestFit,
            row.avoidIf,
            row.reviewSignal,
            row.riskFlags,
            row.evidenceConfidence,
          ],
          query,
        ),
      ),
    [query],
  );

  const filteredPain = useMemo(
    () =>
      painSummary.categories.filter((row) =>
        includesQuery([row.label, row.plainLanguage, row.likelyPaidHelp, row.threadCount, row.commentCount], query),
      ),
    [query],
  );

  const filteredResources = useMemo(
    () =>
      resourceRows.filter((row) =>
        includesQuery([row.title, row.kind, row.path, row.focus, row.rowCount], query),
      ),
    [query],
  );

  const maxStateCount = Math.max(...activeAdultSummary.topStates.map((state) => state.count));
  const maxSignalCount = Math.max(...activeAdultSummary.topSignals.map((signal) => signal.count));
  const maxPainThreads = Math.max(...painSummary.categories.map((pain) => pain.threadCount));

  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="brand-lockup" href="#top" aria-label="Next Chapter Atlas home">
          <span className="brand-mark">
            <Compass size={22} aria-hidden="true" />
          </span>
          <span>
            <strong>{positioning.name}</strong>
            <small>Life-system planning</small>
          </span>
        </a>
        <nav className="top-nav" aria-label="Primary navigation">
          {navigationSections.map((section) => (
            <a href={`#${section.anchor}`} key={section.anchor}>
              {section.label}
            </a>
          ))}
        </nav>
        <a className="header-action" href="#atlas">
          <LayoutDashboard size={18} aria-hidden="true" />
          Atlas
        </a>
      </header>

      <main id="top">
        <section className="hero-section">
          <div className="hero-copy">
            <p className="eyebrow">{positioning.eyebrow}</p>
            <h1>{positioning.headline}</h1>
            <p className="hero-subhead">{positioning.subheadline}</p>
            <div className="hero-actions">
              <a className="primary-action" href="#paths">
                {positioning.callsToAction.primary}
                <ChevronRight size={18} aria-hidden="true" />
              </a>
              <a className="secondary-action" href="#field-notes">
                <BookOpen size={18} aria-hidden="true" />
                {positioning.callsToAction.secondary}
              </a>
            </div>
          </div>

          <div className="hero-dashboard" aria-label="Atlas dashboard summary">
            <div className="dashboard-header">
              <span className="dashboard-kicker">Current Atlas Read</span>
              <span className="freshness">Updated {new Date(painSummary.generatedAt).toLocaleDateString("en-US")}</span>
            </div>
            <div className="metric-strip">
              {dashboardMetrics.map((metric) => (
                <div className="metric-tile" key={metric.label}>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                  <p>{metric.detail}</p>
                </div>
              ))}
            </div>
            <div className="thesis-callout">
              <CircleDollarSign size={22} aria-hidden="true" />
              <p>
                <strong>{painSummary.bestInitialOfferHypothesis}</strong>
                <span>
                  The research points toward a trust-clean dossier product, not another generic retirement
                  calculator.
                </span>
              </p>
            </div>
          </div>
        </section>

        <section className="market-read">
          <div className="section-heading">
            <span className="section-label">Positioning</span>
            <h2>The product is an orientation layer for people who have too many plausible lives.</h2>
          </div>
          <div className="icp-grid">
            {icpCards.map((card) => {
              const Icon = card.icon;
              return (
                <article className="insight-card" key={card.title}>
                  <div className="card-icon">
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <h3>{card.title}</h3>
                  <p>{card.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="section-band" id="paths">
          <div className="section-heading wide">
            <span className="section-label">Life Paths</span>
            <h2>Compare retirement as a set of operating systems.</h2>
            <p>
              The useful question is not which option sounds best. It is which option keeps working when
              money, health, family, energy, and belonging are under pressure.
            </p>
          </div>

          <div className="path-workbench">
            <div className="path-selector" aria-label="Lifestyle path selector">
              {lifestylePaths.map((path) => (
                <button
                  className={path.id === selectedLifestyle.id ? "path-chip active" : "path-chip"}
                  key={path.id}
                  onClick={() => setSelectedPath(path.id)}
                  type="button"
                >
                  {path.shortLabel}
                </button>
              ))}
            </div>

            <article className="path-detail">
              <div>
                <p className="eyebrow compact">Selected path</p>
                <h3>{selectedLifestyle.title}</h3>
                <p>{selectedLifestyle.thesis}</p>
              </div>
              <div className="detail-columns">
                <div>
                  <h4>Best for</h4>
                  <ul>
                    {selectedLifestyle.bestFor.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4>Watchouts</h4>
                  <ul>
                    {selectedLifestyle.watchouts.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4>Field checks</h4>
                  <ul>
                    {selectedLifestyle.fieldChecks.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          </div>

          <div className="path-grid">
            {lifestylePaths.map((path) => (
              <article className="path-card" key={path.id}>
                <div className="path-card-top">
                  <Route size={20} aria-hidden="true" />
                  <span>{path.shortLabel}</span>
                </div>
                <h3>{path.title}</h3>
                <p>{path.thesis}</p>
                <div className="signal-list">
                  {path.goodDaySignals.map((signal) => (
                    <span key={signal}>{signal}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="atlas-section" id="atlas">
          <div className="section-heading wide">
            <span className="section-label">Frankenstein Dashboard</span>
            <h2>Tables first, then narrative. The atlas stays flexible.</h2>
            <p>
              This prototype can absorb new crawls, source maps, score sheets, essays, and comparison
              tables without forcing the idea into one premature product shape.
            </p>
          </div>

          <div className="atlas-toolbar">
            <div className="tab-list" role="tablist" aria-label="Atlas tables">
              {atlasTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    aria-selected={activeTab === tab.id}
                    className={activeTab === tab.id ? "tab-button active" : "tab-button"}
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    role="tab"
                    type="button"
                  >
                    <Icon size={17} aria-hidden="true" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <label className="search-box">
              <Search size={18} aria-hidden="true" />
              <input
                aria-label="Search atlas tables"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search places, risks, resources..."
                value={query}
              />
            </label>
          </div>

          <div className="table-stage">
            {activeTab === "places" && (
              <div className="responsive-table">
                <table>
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Archetype</th>
                      <th>Best fit</th>
                      <th>Avoid if</th>
                      <th>Risk flags</th>
                      <th>Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLocations.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.location}</strong>
                          <span>{row.region}</span>
                        </td>
                        <td>{row.archetype}</td>
                        <td>{row.bestFit.join(", ")}</td>
                        <td>{row.avoidIf.join(", ")}</td>
                        <td>
                          <div className="flag-list">
                            {row.riskFlags.map((flag) => (
                              <span key={flag}>{flag}</span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <div className="evidence-links">
                            {row.evidenceLinks.slice(0, 2).map((link, index) => (
                              <a href={link} key={link} rel="noreferrer" target="_blank">
                                Source {index + 1}
                                <ExternalLink size={13} aria-hidden="true" />
                              </a>
                            ))}
                            <small>{row.evidenceConfidence} confidence</small>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "pain" && (
              <div className="pain-layout">
                <div className="pain-summary">
                  <HeartPulse size={28} aria-hidden="true" />
                  <h3>{formatNumber(painSummary.corpus.threads)} threads, {formatNumber(painSummary.corpus.sampledComments)} sampled comments</h3>
                  <p>
                    The corpus is strongest for anxiety language: money adequacy, family pressure, place
                    regret, healthcare blockers, housing, and loneliness.
                  </p>
                  <div className="source-chip-row">
                    {painSummary.corpus.subreddits.map((source) => (
                      <span key={source}>{source}</span>
                    ))}
                  </div>
                </div>
                <div className="bar-table">
                  {filteredPain.map((pain) => (
                    <article className="bar-row" key={pain.id}>
                      <div>
                        <strong>{pain.label}</strong>
                        <p>{pain.plainLanguage}</p>
                        <span>{pain.likelyPaidHelp.join(" | ")}</span>
                      </div>
                      <div className="bar-meter" aria-label={`${pain.threadCount} threads`}>
                        <span style={{ width: `${Math.max(12, (pain.threadCount / maxPainThreads) * 100)}%` }} />
                      </div>
                      <b>{formatNumber(pain.threadCount)}</b>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "communities" && (
              <div className="community-layout">
                <div className="inventory-summary">
                  <div>
                    <span>Rich rows</span>
                    <strong>
                      {formatNumber(
                        activeAdultSummary.totals.explore55PlusRichRows + activeAdultSummary.totals.where55RichRows,
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Inventory URLs</span>
                    <strong>
                      {formatNumber(
                        activeAdultSummary.totals.explore55PlusInventoryUrls +
                          activeAdultSummary.totals.where55InventoryUrls,
                      )}
                    </strong>
                  </div>
                  <div>
                    <span>Median range</span>
                    <strong>
                      {formatMoney(activeAdultSummary.medians.priceMin)}-{formatMoney(activeAdultSummary.medians.priceMax)}
                    </strong>
                  </div>
                </div>

                <div className="chart-grid">
                  <div className="ranked-list">
                    <h3>Top states</h3>
                    {activeAdultSummary.topStates.map((state) => (
                      <div className="rank-row" key={state.label}>
                        <span>{state.label}</span>
                        <div className="rank-bar">
                          <i style={{ width: `${(state.count / maxStateCount) * 100}%` }} />
                        </div>
                        <b>{state.count}</b>
                      </div>
                    ))}
                  </div>
                  <div className="ranked-list">
                    <h3>Market signals</h3>
                    {activeAdultSummary.topSignals.map((signal) => (
                      <div className="rank-row" key={signal.label}>
                        <span>{signal.label}</span>
                        <div className="rank-bar warm">
                          <i style={{ width: `${(signal.count / maxSignalCount) * 100}%` }} />
                        </div>
                        <b>{signal.count}</b>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="listing-grid">
                  {activeAdultSummary.sampleListings.map((listing) => (
                    <article className="listing-card" key={listing.name}>
                      <div className="listing-head">
                        <span>{listing.source}</span>
                        <a href={listing.url} rel="noreferrer" target="_blank" aria-label={`${listing.name} source`}>
                          <ArrowUpRight size={18} aria-hidden="true" />
                        </a>
                      </div>
                      <h3>{listing.name}</h3>
                      <p>{listing.location}</p>
                      <strong>{listing.price}</strong>
                      <small>{listing.hoa}</small>
                      <p>{listing.note}</p>
                      <div className="signal-list compact-signals">
                        {listing.signals.slice(0, 5).map((signal) => (
                          <span key={signal}>{signal.replaceAll("_", " ")}</span>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "resources" && (
              <div className="responsive-table">
                <table>
                  <thead>
                    <tr>
                      <th>Artifact</th>
                      <th>Kind</th>
                      <th>Focus</th>
                      <th>Rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResources.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.title}</strong>
                          <span>{row.path}</span>
                        </td>
                        <td>{row.kind}</td>
                        <td>{row.focus}</td>
                        <td>{row.rowCount ? formatNumber(row.rowCount) : "Narrative"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="section-band" id="defenses">
          <div className="section-heading wide">
            <span className="section-label">Planning Defenses</span>
            <h2>The best retirement tool is a pause before irreversible trust.</h2>
            <p>
              These checks are the anti-scam, anti-regret layer: incentive clarity, healthcare radius,
              governance, family boundaries, and digital/financial hygiene.
            </p>
          </div>
          <div className="defense-grid">
            {defenses.map((defense) => {
              const Icon = categoryIcon(defense.category);
              return (
                <article className={`defense-card ${defense.category}`} key={defense.id}>
                  <div className="defense-top">
                    <Icon size={20} aria-hidden="true" />
                    <span>{defense.category}</span>
                  </div>
                  <h3>{defense.title}</h3>
                  <p>{defense.trigger}</p>
                  <ul>
                    {defense.practicalMoves.map((move) => (
                      <li key={move}>{move}</li>
                    ))}
                  </ul>
                  <small>{defense.specialistBoundary}</small>
                </article>
              );
            })}
          </div>
        </section>

        <section className="resources-section" id="resources">
          <div className="section-heading">
            <span className="section-label">Resources</span>
            <h2>Use sources for the job they are actually good at.</h2>
          </div>
          <div className="resource-grid">
            {resourceCategories.map((resource) => (
              <article className="resource-card" key={resource.id}>
                <div className="resource-head">
                  <Landmark size={20} aria-hidden="true" />
                  <h3>{resource.title}</h3>
                </div>
                <p>{resource.useFor}</p>
                <div className="source-chip-row">
                  {resource.exampleSources.map((source) => (
                    <span key={source}>{source}</span>
                  ))}
                </div>
                <h4>Ask</h4>
                <ul>
                  {resource.questionsToAsk.map((question) => (
                    <li key={question}>{question}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="bucket-section" id="bucket-list">
          <div className="section-heading wide">
            <span className="section-label">Bucket List</span>
            <h2>Inspiration with an energy budget, not fantasy content.</h2>
            <p>
              The bucket list section is a counterweight to risk planning. It keeps aspiration alive while
              making events, travel, learning, and legacy projects realistic enough to happen.
            </p>
          </div>

          <div className="event-lens-grid">
            {eventLenses.map((lens) => {
              const Icon = lens.icon;
              return (
                <article className="event-lens" key={lens.label}>
                  <Icon size={20} aria-hidden="true" />
                  <h3>{lens.label}</h3>
                  <p>{lens.text}</p>
                </article>
              );
            })}
          </div>

          <div className="bucket-grid">
            {bucketListCategories.map((category) => (
              <article className="bucket-card" key={category.id}>
                <div className="bucket-top">
                  <Sparkles size={20} aria-hidden="true" />
                  <h3>{category.title}</h3>
                </div>
                <p>{category.principle}</p>
                <div className="idea-list">
                  {category.ideas.map((idea) => (
                    <span key={idea}>{idea}</span>
                  ))}
                </div>
                <small>{category.lowFrictionVersion}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="notes-section" id="field-notes">
          <div className="section-heading wide">
            <span className="section-label">Blog / Field Notes</span>
            <h2>Essays that turn the atlas into a point of view.</h2>
            <p>
              The blog layer gives the project a public voice while the tables keep the claims honest.
            </p>
          </div>
          <div className="notes-grid">
            {fieldNotes.map((note) => (
              <article className="note-card" key={note.id}>
                <span>{note.category}</span>
                <h3>{note.title}</h3>
                <p>{note.summary}</p>
                <details>
                  <summary>
                    Read note
                    <ChevronRight size={16} aria-hidden="true" />
                  </summary>
                  {note.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </details>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <strong>{positioning.name}</strong>
          <p>{positioning.promise}</p>
        </div>
        <p>{positioning.disclaimer}</p>
      </footer>
    </div>
  );
}

export default App;
